import { SUPPORTED_LANGS } from "@majourneys/shared";

/** The MaJourneys app on each store. One place, so every badge agrees. */
export const APP_STORE_URL = "https://apps.apple.com/ma/app/majourneys-discover-morocco/id6744748143";
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.majourneys.mobile";

/**
 * What the "Book on the app" QR encodes: one short link that sends each phone
 * to its own store (see `appRedirectResponse`). Short on purpose, a shorter
 * URL is a smaller, easier-to-scan code.
 */
export const APP_GET_URL = "https://majourneys.com/app";

export type AppStore = "ios" | "android";

export function storeUrl(store: AppStore): string {
  return store === "ios" ? APP_STORE_URL : PLAY_STORE_URL;
}

/**
 * Which store a browser belongs to. iPadOS 13+ reports itself as a Mac, so a
 * "Macintosh" with a touch screen is not knowable from the user agent alone;
 * those land on the homepage, where the store badges are.
 */
export function storeForUserAgent(ua: string | null | undefined): AppStore | null {
  if (!ua) return null;
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  return null;
}

const LANG_ALT = SUPPORTED_LANGS.join("|");
const APP_PATH_RE = new RegExp(`^/(?:(${LANG_ALT})/)?app/?$`);

/**
 * `/app` and `/<lang>/app`: a 302 to the App Store for iPhones and iPads, to
 * Google Play for Android, and to the homepage for everyone else.
 *
 * 302 and `no-store` because the answer depends on the device: a cached or
 * permanent redirect would send the next visitor to the first one's store.
 * `noindex` because it is a doorway, not a page (it is not in the sitemap).
 * Returns null for any other path.
 */
export function appRedirectResponse(pathname: string, userAgent: string | null): Response | null {
  const m = APP_PATH_RE.exec(pathname);
  if (!m) return null;
  const store = storeForUserAgent(userAgent);
  const location = store ? storeUrl(store) : `/${m[1] ?? "en"}`;
  return new Response(null, {
    status: 302,
    headers: {
      Location: location,
      "Cache-Control": "private, no-store",
      Vary: "User-Agent",
      "X-Robots-Tag": "noindex, nofollow",
    },
  });
}
